import React, { useRef, useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Spinner } from "react-bootstrap";
import { FaImage } from "react-icons/fa";
import { Card, CardHeader, CardBody } from "reactstrap";
import { uploadLogo } from "../../../api/profile";
import axios from "axios";
import config from "../../../config";
import { updateProfile, getProfile } from "../../../api/profile";

import { successNotify, errorNotify } from "../../../utils/toast";
import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSelector";

const API_URL = config.API_URL;

interface UploadResponse {
  imageUrl: string;
}

const FileUpload: React.FC = () => {
  const showSnackbar = useSnackbar();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const userProfileData = await getProfile();

      if (userProfileData && userProfileData?.data?.logoUrl) {
        setLogoUrl(userProfileData?.data?.logoUrl);
      }
    };

    fetchProfile();
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files && e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      setFile(droppedFile);
      setPreview(URL.createObjectURL(droppedFile));
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const uploadImage = async (formData: any): Promise<UploadResponse> => {
    try {
      const response: UploadResponse = await axios.post(
        `${API_URL}api/profile/upload-logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      return response;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("Select a logo first!");

    const formData = new FormData();
    formData.append("image", file);

    try {
      setLoading(true);
      const res: any = await uploadImage(formData);
      const uploadLogoUrl = res?.imageUrl;
      setLoading(false);
      if (res.success == false) {
        showSnackbar(res.message, "error");
      } else {
        showSnackbar(res.message, "info");
      }

      const response: any = await updateProfile(uploadLogoUrl);
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleRemoveImage = () => {
    setFile(null);
    setPreview(null);

    // Reset the file input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      <Card>
        <CardHeader className="card-header">{t("Logo")}</CardHeader>
        <CardBody className="justify-content-center">
          <Container className="px-2 px-xl-5">
            <Row className="g-2">
              <Col lg={6}>
                <span>{t("Logo")}</span>
              </Col>
              <Col lg={6}>
                {preview ? (
                  <div style={{ marginBottom: "10px", textAlign: "center" }}>
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                ) : logoUrl ? (
                  <div style={{ marginBottom: "10px", textAlign: "center" }}>
                    <img
                      src={logoUrl}
                      alt="Logo"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                ) : null}
              </Col>
            </Row>

            <Row className="mt-4 g-2">
              <Col lg={6}>
                <span>{t("Upload Your Logo")}</span>
              </Col>
              <Col lg={6}>
                {/* Upload Section */}
                <Form.Group
                  style={{
                    border: "2px dashed #007bff",
                    borderRadius: "8px",
                    padding: "30px",
                    textAlign: "center",
                    position: "relative",
                    cursor: "pointer",
                    backgroundColor: "#f8f9fa",
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <>
                    {/* Image Icon */}
                    <FaImage size={50} color="#6c757d" />
                    <div
                      style={{
                        fontSize: "16px",
                        color: "#6c757d",
                        marginTop: "10px",
                      }}
                    >
                      {t("Drag and Drop your file here or click to select")}
                    </div>
                  </>

                  <Form.Control
                    type="file"
                    accept="image/*"
                    ref={fileInputRef} // Attach ref to the file input
                    onChange={handleFileChange}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4 gap-3">
              <Button variant="danger" onClick={handleRemoveImage}>
                {t("Remove Image")}
                <i className="bi bi-trash ms-2"></i>
              </Button>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={loading}
              >
                {t("Upload")}
                {loading ? (
                  <Spinner animation="border" size="sm" className="ms-2" />
                ) : (
                  <i className="bi bi-file-arrow-up-fill ms-2"></i>
                )}
              </Button>
            </div>
          </Container>
        </CardBody>
      </Card>
    </div>
  );
};

export default FileUpload;
